import React from 'react';
import { Play, Volume2 } from 'lucide-react';
import { motion } from 'framer-motion';
import { useCountUp } from '../hooks/useCountUp';
import { useScrollAnimation } from '../hooks/useScrollAnimation';
export function HeroSection() {
  const {
    ref,
    isVisible
  } = useScrollAnimation(0.1);
  const count = useCountUp(100, 2500, isVisible);
  return <section ref={ref} className="relative pt-8 pb-16 md:pt-16 md:pb-24 overflow-hidden flex flex-col items-center justify-center" data-id="element-94">
      {/* Background Effects */}
      <div className="absolute inset-0 bg-glow-radial opacity-50 pointer-events-none" data-id="element-95" />
      <div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-20 pointer-events-none" data-id="element-96" />

      <div className="container mx-auto px-4 relative z-10 text-center max-w-5xl" data-id="element-97">
        {/* Brand */}
        <motion.div initial={{
        opacity: 0,
        y: -10
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.5
      }} className="mb-4 md:mb-6" data-id="element-98">
          <div className="inline-flex items-center justify-center gap-2" data-id="element-99">
            <span className="font-sora font-extrabold text-xl md:text-2xl tracking-tighter" data-id="element-100">
              Techain<span className="text-purple-500" data-id="element-101">AdAngle</span>
            </span>
          </div>
        </motion.div>

        {/* Headline */}
        <motion.h1 initial={{
        opacity: 0,
        scale: 0.95
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        duration: 0.6,
        delay: 0.1
      }} className="font-extrabold leading-[1.1] mb-4 md:mb-6" data-id="element-102">
          <span className="block text-2xl md:text-4xl text-white mb-1 md:mb-2" data-id="element-103">
            Generá
          </span>
          <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-purple-600 block text-4xl md:text-7xl drop-shadow-[0_0_30px_rgba(139,92,246,0.3)] mb-2 md:mb-4" data-id="element-104">
            +{count} Creativos Que Venden
          </span>
          <span className="block text-2xl md:text-4xl text-white" data-id="element-105">
            En Solo 10 Minutos
          </span>
        </motion.h1>

        {/* Subheadline */}
        <motion.p initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        duration: 0.6,
        delay: 0.2
      }} className="text-gray-400 text-base md:text-xl max-w-2xl mx-auto mb-6 md:mb-8 leading-relaxed px-2" data-id="element-106">
          Generá{' '}
          <span className="text-white font-medium" data-id="element-107">
            imágenes estáticas listas para Meta Ads
          </span>{' '}
          con los ángulos de venta que realmente convierten.
        </motion.p>

        {/* Sound Warning */}
        <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        delay: 0.3
      }} className="inline-flex items-center gap-2 bg-white/5 border border-white/10 rounded-full px-3 py-1.5 text-xs md:text-sm text-gray-300 mb-6 md:mb-8" data-id="element-108">
          <Volume2 className="w-3 h-3 md:w-4 md:h-4 text-purple-400" data-id="element-109" />
          <span data-id="element-110">Activá el sonido 🔊</span>
        </motion.div>

        {/* Video Container */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6,
        delay: 0.4
      }} className="relative w-full max-w-3xl mx-auto aspect-video bg-black rounded-xl md:rounded-2xl border border-purple-500/30 shadow-[0_0_40px_rgba(139,92,246,0.15)] overflow-hidden mb-8" data-id="element-111">
          <iframe src="https://fast.wistia.net/embed/iframe/cxh93bsdbp?web_component=true&seo=false" title="Techain Ad Angle Demo" allow="autoplay; fullscreen" allowTransparency={true} frameBorder="0" scrolling="no" className="absolute inset-0 w-full h-full" data-id="element-112" />
        </motion.div>

        {/* CTA */}
        <motion.div initial={{
        opacity: 0,
        scale: 0.95
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        duration: 0.5,
        delay: 0.5
      }} className="flex flex-col items-center gap-3 md:gap-4 w-full px-4" data-id="element-113">
          <a href="#pricing" className="w-full max-w-xl bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-500 hover:to-indigo-500 text-white font-bold text-lg md:text-2xl py-4 md:py-6 px-6 md:px-8 rounded-xl shadow-[0_10px_40px_rgba(139,92,246,0.4)] transform hover:-translate-y-1 transition-all duration-300 animate-pulse-glow text-center" data-id="element-114">
            ACCEDER A LA APP →
          </a>

          {/* Social Proof below CTA */}
          <div className="flex items-center justify-center gap-2 mt-2" data-id="element-115">
            <div className="flex -space-x-2" data-id="element-116">
              <div className="w-6 h-6 rounded-full bg-blue-500 border-2 border-black" data-id="element-117"></div>
              <div className="w-6 h-6 rounded-full bg-purple-500 border-2 border-black" data-id="element-118"></div>
              <div className="w-6 h-6 rounded-full bg-emerald-500 border-2 border-black" data-id="element-119"></div>
            </div>
            <p className="text-xs md:text-sm text-gray-400" data-id="element-120">
              <span className="text-white font-bold" data-id="element-121">+200 media buyers</span> ya
              escalan con Techain
            </p>
          </div>

          <p className="text-xs md:text-sm text-gray-500 mt-1" data-id="element-122">
            <span className="text-emerald-500 mr-1" data-id="element-123">●</span>
            Oferta de Lanzamiento:{' '}
            <span className="text-gray-300 font-bold" data-id="element-124">$77.700 ARS</span> (Pago
            Único)
          </p>
        </motion.div>
      </div>
    </section>;
}